import React, { useState, useRef, useEffect } from "react";
import { Controller } from "react-hook-form";
import {
  DropZone,
  FileTrigger,
  Button,
  Text,
  Group,
} from "react-aria-components";
import type { DropEvent, FileDropItem } from "react-aria";
import {
  DocumentIcon,
  XMarkIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/react";

import { FieldComponentProps } from "./types";
import FieldLabel from "./FieldLabel";
import FieldDescription from "./FieldDescription";
import FieldError from "./FieldError";
import ErrorMessage from "components/ErrorMessage";
import { useFormConfig } from "components/Form/hooks";
import { UploadError, xhrUpload } from "hooks/useXHRUpload";
import { useDeviceCapabilities } from "hooks/useDeviceCapabilities";
import { formatFileSize, getFileExtension, processFile } from "helpers/files";

// Common file type constants
const IMAGE_FILE_TYPES = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];
const DOCUMENT_FILE_TYPES = [".pdf"];

const DEFAULT_ALLOWED_TYPES = [...IMAGE_FILE_TYPES, ...DOCUMENT_FILE_TYPES];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

type UploadStatus = "idle" | "uploading" | "success" | "error";

interface UploadState {
  status: UploadStatus;
  progress: number;
  error?: string;
}

interface AttachmentValue {
  file_uri: string;
  file_name: string;
  file_size?: number;
  preview_blob?: string;
}

type FormValues = {
  [key: string]: AttachmentValue;
};

const AttachmentField: React.FC<FieldComponentProps<FormValues>> = ({
  field,
  control,
}) => {
  const { supportsDragAndDrop, isMobile, hasCamera } = useDeviceCapabilities();

  const { config: formConfig, responseId } = useFormConfig();
  const isRequired = field.validations?.required || false;
  const allowedTypes =
    field.properties?.allowed_file_types || DEFAULT_ALLOWED_TYPES;
  const allowMultiple = field.properties?.allow_multiple_files || false;
  const maxFileSize = field.properties?.max_file_size || MAX_FILE_SIZE;

  // Check if image files are allowed
  const allowsImages = allowedTypes.some((type) =>
    IMAGE_FILE_TYPES.includes(type.toLowerCase()),
  );

  const [uploadState, setUploadState] = useState<UploadState>({
    status: "idle",
    progress: 0,
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const uploadFile = async (
    file: File,
  ): Promise<{ file_uri: string; file_name: string }> => {
    setUploadState({ status: "uploading", progress: 0 });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const metadata = {
        form_id: formConfig.id,
        field_ref: field.ref,
        response_id: responseId,
      };
      formData.append("metadata", JSON.stringify(metadata));

      const response = await xhrUpload<{ file_uri: string; file_name: string }>(
        "/forms/uploads",
        formData,
        {
          onProgress: (progress) => {
            setUploadState((prev) => ({
              ...prev,
              progress,
            }));
          },
        },
      );

      setUploadState({ status: "success", progress: 100 });
      return response;
    } catch (error) {
      const rejectedByApi =
        error instanceof UploadError &&
        error.status >= 400 &&
        error.status < 500;

      if (rejectedByApi) {
        // processFile already applied the same rules before calling us, so a
        // rejection here means the two disagree and the person had no way to
        // avoid it. The filename is withheld — these are often photo IDs.
        Sentry.captureMessage(
          "Upload rejected by the API after passing client-side validation",
          {
            level: "warning",
            extra: {
              status: (error as UploadError).status,
              error_code: (error as UploadError).errorCode,
              form_id: formConfig.id,
              field_ref: field.ref,
              response_id: responseId,
              extension: getFileExtension(file.name),
              mime_type: file.type,
              size_bytes: file.size,
              allowed_file_types: allowedTypes,
            },
          },
        );
      } else {
        Sentry.captureException(error, {
          extra: {
            status: error instanceof UploadError ? error.status : undefined,
            form_id: formConfig.id,
            field_ref: field.ref,
            response_id: responseId,
          },
        });
      }

      setUploadState({
        status: "error",
        progress: 0,
        error: rejectedByApi
          ? (error as UploadError).message
          : "We couldn't upload your file. Please check your connection and try again.",
      });
      throw error;
    }
  };

  return (
    <Controller
      control={control}
      name={field.ref}
      rules={{
        required: isRequired ? "This field is required." : false,
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const handleFilesFromDrop = async (e: DropEvent) => {
          const fileDropItems = e.items.filter(
            (item): item is FileDropItem => item.kind === "file",
          );

          try {
            const files = await Promise.all(
              fileDropItems.map((item) => item.getFile()),
            );
            const file = files[0];

            // DropZone also routes pasted clipboard items here, and a paste of
            // text carries no file at all.
            if (!file) {
              return;
            }

            await processFile(file, {
              maxFileSize,
              allowedFileTypes: allowedTypes,
              onUploadStateChange: setUploadState,
              uploadFile,
              onChange,
              onPreviewGenerated: setPreviewUrl,
            });
          } catch (error) {
            console.error("Error handling file:", error);
          }
        };

        const handleFilesFromSelect = async (files: FileList | null) => {
          if (files?.length) {
            const file = files[0];

            await processFile(file, {
              maxFileSize,
              allowedFileTypes: allowedTypes,
              onUploadStateChange: setUploadState,
              uploadFile,
              onChange,
              onPreviewGenerated: setPreviewUrl,
            });
          } else {
            onChange(null);
            setPreviewUrl(null);
          }
        };

        const handleRemove = () => {
          onChange(null);
          setPreviewUrl(null);
          setUploadState({ status: "idle", progress: 0 });
        };

        const handleDropZoneClick = (e: React.MouseEvent) => {
          // Only trigger if clicking the drop zone itself, not its children
          if (e.target === e.currentTarget) {
            triggerRef.current?.click();
          }
        };

        // When the component mounts or value changes, set the preview URL
        useEffect(() => {
          if (value?.preview_blob) {
            setPreviewUrl(value.preview_blob);
          } else {
            setPreviewUrl(null);
          }
        }, [value]);

        return (
          <div className="mb-4">
            <FieldLabel text={field.title} />
            <FieldDescription content={field.properties?.description} />

            <Text
              slot="description"
              className="mb-2 inline-block text-sm text-soil"
            >
              Allowed file types: {allowedTypes.join(", ")}
            </Text>

            {!value ? (
              <Group>
                <div
                  onClick={handleDropZoneClick}
                  tabIndex={-1}
                  data-autofocus="true"
                  className="outline-none"
                >
                  <DropZone
                    onDrop={handleFilesFromDrop}
                    className="relative cursor-pointer rounded border border-dashed border-gray-400 bg-oat-50 p-8 text-center transition-colors hover:border-gray-600 hover:bg-oat-100"
                  >
                    {({ isHovered }) => (
                      <div
                        className={`flex flex-col items-center justify-center space-y-4 ${
                          isHovered ? "border-green-500" : ""
                        }`}
                      >
                        <CloudArrowUpIcon className="size-16 text-soil" />
                        <div className="flex flex-col items-center space-y-2">
                          <div className="flex items-center gap-1">
                            <span className="font-bold">
                              Choose {allowMultiple ? "files" : "a file"}
                            </span>
                            {supportsDragAndDrop && (
                              <div className="sm:inline-block">
                                <span className="whitespace-nowrap"> or </span>
                                <span className="font-bold">
                                  drag and drop here
                                </span>
                              </div>
                            )}
                            {isMobile && hasCamera && allowsImages && (
                              <div className="sm:inline-block">
                                <span className="whitespace-nowrap"> or </span>
                                <span className="font-bold">take a photo</span>
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-soil">
                            Size limit: {formatFileSize(maxFileSize)}
                          </div>
                        </div>
                        <FileTrigger
                          acceptedFileTypes={allowedTypes}
                          onSelect={handleFilesFromSelect}
                          allowsMultiple={allowMultiple}
                        >
                          <Button
                            ref={triggerRef}
                            className="absolute inset-0 h-full w-full opacity-0"
                          >
                            Open file browser
                          </Button>
                        </FileTrigger>
                      </div>
                    )}
                  </DropZone>
                </div>
              </Group>
            ) : (
              <div className="relative rounded border border-gray-200 p-4">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="File preview"
                    className="mx-auto max-h-48 object-contain"
                  />
                ) : (
                  <div className="flex items-center justify-center">
                    <DocumentIcon className="h-12 w-12 text-soil" />
                  </div>
                )}
                <div className="mt-2 text-center text-sm font-medium text-soil">
                  {value.file_name}
                  {value.file_size && (
                    <>
                      <span className="mx-2 text-gray-400">-</span>
                      <span className="text-gray-600">
                        {formatFileSize(value.file_size)}
                      </span>
                    </>
                  )}
                </div>
                <button
                  onClick={handleRemove}
                  className="absolute right-2 top-2 rounded-full bg-white p-1 text-soil hover:text-cantelope"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}

            {uploadState.status === "uploading" && (
              <div className="mt-2">
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full bg-cantelope transition-all"
                    style={{ width: `${uploadState.progress}%` }}
                  />
                </div>
              </div>
            )}

            {uploadState.status === "error" && (
              <ErrorMessage className="mt-4">{uploadState.error}</ErrorMessage>
            )}

            {error && <FieldError>{error.message}</FieldError>}
          </div>
        );
      }}
    />
  );
};

export default AttachmentField;
